export enum ProductUnavailableReasonCodes {
  ALCOHOL_LESS_THAN_REQUIRED_QUANTITY = 'ALCOHOL_LESS_THAN_REQUIRED_QUANTITY',
  ALCOHOL_PROHIBITED_PERIOD = 'ALCOHOL_PROHIBITED_PERIOD',
  EXPIRED_ALCOHOL_LICENSE = 'EXPIRED_ALCOHOL_LICENSE',
  UNAVAILABLE_IN_RM3 = 'UNAVAILABLE_IN_RM3',
  MORE_THAN_MAX_REQUIRED = 'MORE_THAN_MAX_REQUIRED'
}

export enum CollectionType {
  NONE = 'NONE',
  LOYALTY = 'LOYALTY',
  PROMOTION = 'PROMOTION',
  IMAGE_LEFT_RIBBON = 'IMAGE_LEFT_RIBBON',
  IMAGE_RIGHT_RIBBON = 'IMAGE_RIGHT_RIBBON',
  PROPOINT_PROMOTION = 'PROPOINT_PROMOTION'
}

export type ShoppingDataLayerSellerDetail = {
  shopId: number;
  shopName: string;
  fulfilledByPlatform: string;
  marketplaceType: string;
};

export type ShoppingDataLayerShippingInfo = {
  zone: string;
  shippingPrice: number;
  deliveryTimeEarliestDays: number;
  deliveryTimeLatestDays: number;
  deliveryTimeEarliestDate: string;
  deliveryTimeLatestDate: string;
};

export type ShoppingDataLayerProductMeasurement = {
  height: number;
  length: number;
  width: number;
  dimensionUnit: string;
  weight: number;
  weightUnit: string;
};

export type ShoppingDateLayerMetafield = {
  id: string;
  key: string;
  value: string;
};

export type ShoppingDataLayerProductVariant = {
  id: string;
  outsideOfDeliveryZone: boolean;
  storeCode: string;
  sku: string;
  price: string;
  compareAtPrice: string;
  storedPrice: string;
  storedCompareAtPrice: string;
  inventoryQuantity: number;
  discountStartDate: string;
  discountEndDate: string;
  metafields: ShoppingDateLayerMetafield[];
  miraklOfferId: string;
  isDropShipment: boolean;
  status: string;
  moq: number;
  totalInventory: number;
  isPreOrder: boolean;
  costPrice: number;
  rmsStatus: string;
  isWeightedFresh: boolean;
  isExpressDelivery: boolean;
};

export type ShoppingDataLayerProductPrice = {
  amount: string;
  currencyCode: string;
};

export type ShoppingDataLayerProductPriceRange = {
  minVariantPrice: ShoppingDataLayerProductPrice;
  maxVariantPrice: ShoppingDataLayerProductPrice;
};

export type ShoppingDataLayerProductImage = {
  id: string;
  originalSrc: string;
};

export type ShoppingDataLayerProductPromotionConditionDetail = {
  targetType: string;
  targetValue: number;
  rewardType: string;
  rewardValue: number;
};

export type ShoppingDataLayerProductPromotion = {
  ruleId: string;
  groupId: string;
  type: string;
  conditionType: string;
  conditionDetail: ShoppingDataLayerProductPromotionConditionDetail;
  is3P: boolean;
  terms: string;
  skuCount: number;
  maxCap: number;
};

export type ShoppingDataLayerProductCollection = {
  id: string;
  promotion: ShoppingDataLayerProductPromotion;
  handle: string;
  title: string;
  titleAlt: string;
  startDate: string;
  endDate: string;
  imageUrl: string;
  displayOrder: number;
  type: string;
  promotionPosition: string;
  description: string;
  imageUrlTh: string;
  storeCode: string;
  isStoreDependent: boolean;
  active: boolean;
  frameEn?: string;
  frameTh?: string;
  hasFrame?: boolean;
};

export type ShoppingDataLayerProductBadge = {
  id: string;
  handle: string;
  name: string;
};

export type ShoppingDataLayerProductSlabPriceTiers = {
  quantity: number;
  tier: number;
  discount: number;
  priceInVat: number;
};

export type ShoppingDataLayerProductSlabPrices = {
  slabPriceDescription: string;
  slabPriceTiers: ShoppingDataLayerProductSlabPriceTiers[];
};

export type ShoppingDataLayerProductUpcharge = {
  percentage: number;
  minimumThreshold: number;
};

type ShoppingDataLayerDeliveryDescription = {
  singleLine: string;
  summary: string;
  popup: string;
};

export type ShoppingDataLayerProductUnavailableReason = {
  code: string;
  message: string;
};

export type ShoppingDataLayerProductCategoryTree = {
  L1: string;
  L2: string;
  L3: string;
};

export type ShoppingDataLayerProductBrandDetailsModel = {
  flexiPageHandle: string;
  displayName: string;
  logoUrl: string;
};

export type ShoppingDataLayerProduct = {
  id: string;
  storeFrontId: string;
  title: string;
  productType: string;
  brand: string;
  status: string;
  sku: string;
  barcode: string;
  skuCode: string;
  shelfNumber: string;
  seller: string;
  size: string;
  priceUnit: string;
  totalInventory: number;
  description: string;
  vendor: string;
  discountStartDate: string;
  discountEndDate: string;
  displayPrice: number;
  originPrice: number;
  inVatMsrpPrice: number;
  inVatPriceOriginal: number;
  minVariantPrice: string;
  maxVariantPrice: string;
  productInBasketCount?: number;
  sellerDetails: ShoppingDataLayerSellerDetail;
  shippingInfo: ShoppingDataLayerShippingInfo[];
  measurements: ShoppingDataLayerProductMeasurement;
  variants: ShoppingDataLayerProductVariant[];
  priceRange: ShoppingDataLayerProductPriceRange;
  images: ShoppingDataLayerProductImage[];
  imageUrls: string[];
  metafields: ShoppingDateLayerMetafield[];
  collections: ShoppingDataLayerProductCollection[];
  badges: ShoppingDataLayerProductBadge[];
  storageInformation: string;
  expiryDays: number;
  productSku: string;
  makroId: string;
  slabPrices: ShoppingDataLayerProductSlabPrices;
  moq: number;
  isWeightedFresh: boolean;
  isBulky: boolean;
  isPreOrder: boolean;
  installmentType: string;
  upcharge: ShoppingDataLayerProductUpcharge;
  deliveryIn24Hrs: boolean;
  isSalesCustomer: boolean;
  isMakroPro: boolean;
  is1pHybrid: boolean;
  isAlcohol: boolean;
  isUltraFresh: boolean;
  sellInPiece: boolean;
  deliveryDescription: ShoppingDataLayerDeliveryDescription;
  is1PX: boolean;
  itemBarCode: string;
  itemBarCodes: string[];
  unitFactor: number;
  mainImage: string;
  miraklProductId: string;
  units: string;
  unitType: string;
  unitLength: string;
  unitWidth: string;
  unitHeight: string;
  packagingLength: string;
  packagingWidth: string;
  packagingHeight: string;
  packagingWeight: string;
  providerSku: string;
  keywords: string;
  misspellKeywords: string;
  boostedScore: number;
  boostStartDate: string;
  boostEndDate: string;
  applyBoostingTo: string[];
  boostedReason: string;
  unitNetContent: string;
  unitNetContentUnit: string;
  titleTh: string;
  titleEn: string;
  shortDescriptionTh: string;
  shortDescriptionEn: string;
  longDescriptionTh: string;
  longDescriptionEn: string;
  rmsDescTh: string;
  rmsDescEn: string;
  brandTh: string;
  brandEn: string;
  volume: number;
  supplierPackSize: number;
  unavailableReason: ShoppingDataLayerProductUnavailableReason;
  categoryTree: ShoppingDataLayerProductCategoryTree;
  brandDetails: ShoppingDataLayerProductBrandDetailsModel;
};

export type ProductStatusDataType = {
  storeCodes?: string[];
  alcoholExpiryDate?: string;
  enable3pOwnfleet?: boolean;
};
